import React, { useCallback } from 'react';

import useOauthCreateUser from '@hooks/useOauthCreateUser';
import useOauthLogin from '@hooks/useOauthLogin';
import useOauthUnlockContent from '@hooks/useOauthUnlockContent';
import useServerSideRendering from '@hooks/useServerSideRendering';
import { GoogleLogin, GoogleOAuthProvider } from '@react-oauth/google';
import { GOOGLE_CLIENT_ID } from '@src/env';
import { GOOGLE_PROVIDER, GoogleEventName } from '@utils/backend-api/oauth';
import { LeadType } from '@utils/types';

interface MarketingLeadCaptureWithGoogleProps {
  articleId?: number;
  briefingGuid?: string;
  buttonWidth?: number;
  eventName: GoogleEventName;
  formLocation: string;
  isGdprOptIn: boolean;
  leadType: LeadType;
  newsletterIds?: number[];
  redirectToOnSuccess?: string;
  shareGuid?: string;
  text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
}

export default function MarketingLeadCaptureWithGoogle({
  articleId,
  briefingGuid,
  buttonWidth = 250,
  eventName,
  formLocation,
  isGdprOptIn,
  leadType,
  newsletterIds,
  redirectToOnSuccess,
  shareGuid,
  text = 'continue_with',
}: MarketingLeadCaptureWithGoogleProps) {
  const { login } = useOauthLogin();
  const { createUser } = useOauthCreateUser();
  const { unlockContentViaOauth } = useOauthUnlockContent();
  const hydrated = useServerSideRendering();

  // Memoize the Google callback handler
  const handleGoogleCallback = useCallback(
    (response) => {
      const idToken = response.credential;
      const commonPayload = {
        form_location: formLocation,
        id_token: idToken,
        lead_type: leadType,
        newsletter_ids: newsletterIds,
        page: window.location.pathname,
        provider: GOOGLE_PROVIDER,
      };

      if (eventName === GoogleEventName.LogIn) {
        login({
          ...commonPayload,
          returning_url: redirectToOnSuccess,
        });
      } else if (eventName === GoogleEventName.CreateLead) {
        createUser({
          ...commonPayload,
          declined_opt_in: isGdprOptIn,
          opt_in_required: isGdprOptIn,
          redirect_to_on_success: redirectToOnSuccess,
        });
      } else if (eventName === GoogleEventName.UnlockContent) {
        unlockContentViaOauth({
          ...commonPayload,
          article_id: articleId,
          briefing_guid: briefingGuid,
          opt_in_required: isGdprOptIn,
          share_guid: shareGuid,
          user: {
            declined_opt_in: isGdprOptIn,
          },
        });
      }
    },
    [
      eventName,
      login,
      createUser,
      unlockContentViaOauth,
      leadType,
      formLocation,
      redirectToOnSuccess,
      isGdprOptIn,
      articleId,
      briefingGuid,
      shareGuid,
      newsletterIds,
    ]
  );

  return (
    <>
      {hydrated && (
        <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
          <GoogleLogin
            text={text}
            width={buttonWidth}
            theme="outline"
            logo_alignment="left"
            type="standard"
            onSuccess={(response) => handleGoogleCallback(response)}
          />
        </GoogleOAuthProvider>
      )}
    </>
  );
}
